import { useMemo, useRef, useState } from 'react';
import { ArrowLeft, Camera, ChevronRight, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { InfoHint } from '@/components/ui/info-hint';
import { LabeledSection } from '@/components/ui/labeled-section';
import { Markdown } from '@/components/ui/markdown';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

import { AgentTile } from '../components/AgentTile';
import { InstallFormDialog } from '../components/InstallFormDialog';
import { InstallStatusBadge, PrivateLock } from '../components/AgentStatusBadge';
import { DIRECTION_LABELS } from '../constants';
import {
  useAgentInstalls,
  useAgentTemplate,
  useAgentTemplates,
  useDeleteAgentInstall,
  useSetAgentImage,
} from '../queries';
import type { AgentInstall, AgentTemplate } from '../types';

const HOW_TO_USE =
  'Open this agent in Claude — via Claude Code, the desktop app, or the API — and it works ' +
  "from your organization's knowledge to handle its area. An admin installs it and sets the " +
  'tools and knowledge it may use.';

/**
 * Where the page is reached from — decides the back-link target and whether the
 * admin management actions (Install / Edit / Remove) render. `admin` = Settings
 * › Agents (management); `team` = the read-only AI Team directory.
 */
type DetailContext = 'admin' | 'team';

/**
 * Collapsible variant of LabeledSection — an uppercase heading that expands/collapses
 * its body. Used for the long main-column blocks (Quickstart open by default,
 * Operating manual collapsed).
 */
function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-2">
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 text-xs font-semibold tracking-wider uppercase transition-colors">
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Shared, agent-agnostic steps for actually running the agent in Claude. Rendered
 * at the top of every agent's Quickstart so activation reads the same everywhere
 * (the per-agent quickstart below covers *what* to ask it).
 */
function RunInClaude({ code, name }: { code: string; name: string }) {
  return (
    <div className="border-primary/20 bg-primary/5 space-y-3 rounded-md border p-6">
      <h4 className="text-sm font-semibold">Run {name} in Claude</h4>
      <ol className="text-muted-foreground list-decimal space-y-2 pl-5 text-sm">
        <li>
          <span className="text-foreground font-medium">Connect Dev13 once.</span> Add Dev13 as an
          MCP connector in Claude Code, the desktop app, or via the API — one-time per person.
        </li>
        <li>
          <span className="text-foreground font-medium">Call the agent by name.</span> In a Claude
          chat, say{' '}
          <code className="bg-muted rounded px-1 py-0.5 text-xs">Use the Dev13 {code} agent</code>.
          Claude loads {name}&apos;s playbook, tools, and your organization&apos;s knowledge.
        </li>
        <li>
          <span className="text-foreground font-medium">Give it the task.</span> Name the action and
          inputs — copy one of the example prompts below.
        </li>
        <li>
          <span className="text-foreground font-medium">Save the output (optional).</span> Ask Claude
          to save the result; it appears under{' '}
          <span className="text-foreground font-medium">Results</span>.
        </li>
      </ol>
    </div>
  );
}

/**
 * Full-page detail for a single agent — replaces the old slide-out drawer. Shows
 * the summary ("what it does"), a short "how to use" blurb, details (tools / KB
 * tags / install state / versions), and the latest version's operating manual
 * (`prompt_md`) as markdown. The manual is fetched on mount via
 * `useAgentTemplate`. In the `admin` context (and for Founder/Super-Admin) the
 * header exposes Install / Edit / Remove; the `team` context is read-only.
 *
 * The template is resolved by `:code` from the (cached) catalog list, and its
 * install by matching `template_code` in the installs list — so the page works
 * on a direct load, not only when navigated to from a list.
 */
export function AgentDetailPage({ context }: { context: DetailContext }) {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const canManage = context === 'admin' && (role === 'founder' || role === 'super_admin');
  const backTo = context === 'admin' ? '/settings/agents' : '/ai-team';

  const templatesQuery = useAgentTemplates();
  const installsQuery = useAgentInstalls();

  const template = useMemo(
    () => (templatesQuery.data ?? []).find((t) => t.code === code) ?? null,
    [templatesQuery.data, code],
  );
  const install = useMemo(
    () => (installsQuery.data ?? []).find((i) => i.template_code === code) ?? null,
    [installsQuery.data, code],
  );

  const detailQuery = useAgentTemplate(template?.id ?? null);
  const detail = detailQuery.data;

  // Admin action state — Install/Edit dialog + delete confirmation.
  const [dialogTemplate, setDialogTemplate] = useState<AgentTemplate | null>(null);
  const [dialogInstall, setDialogInstall] = useState<AgentInstall | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteInstall = useDeleteAgentInstall();
  const setImage = useSetAgentImage();
  const fileRef = useRef<HTMLInputElement>(null);

  const openInstall = () => {
    if (!template) return;
    setDialogInstall(null);
    setDialogTemplate(template);
    setDialogOpen(true);
  };
  const openEdit = () => {
    if (!template || !install) return;
    setDialogTemplate(template);
    setDialogInstall(install);
    setDialogOpen(true);
  };

  const isPending = templatesQuery.isPending || installsQuery.isPending;

  if (isPending) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(backTo)}>
          <ArrowLeft className="size-4" /> Back
        </Button>
        <p className="text-muted-foreground py-10 text-center text-sm">
          This agent could not be found. It may have been removed from the catalog.
        </p>
      </div>
    );
  }

  const versions = detail?.versions ?? template.versions ?? [];
  const latest = detail?.versions.at(-1) ?? null;
  const quickstart = detail?.quickstart_md?.trim();

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate(backTo)}>
        <ArrowLeft className="size-4" /> {context === 'admin' ? 'Agents' : 'AI Team'}
      </Button>

      {/* Header — identity + (admin) management actions. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {canManage ? (
            <div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={setImage.isPending}
                title="Change image (applies to all organizations)"
                aria-label="Change agent image"
                className="group focus-visible:ring-ring relative rounded-lg focus-visible:ring-1 focus-visible:outline-none"
              >
                <AgentTile
                  code={template.code}
                  direction={template.direction}
                  imageUrl={template.image}
                  className="size-12 text-base"
                />
                <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/45 opacity-0 transition group-hover:opacity-100">
                  <Camera className="size-4 text-white" />
                </span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setImage.mutate({ id: template.id, file });
                  e.target.value = '';
                }}
              />
            </div>
          ) : (
            <AgentTile
              code={template.code}
              direction={template.direction}
              imageUrl={template.image}
              className="size-12 text-base"
            />
          )}
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{template.name}</h1>
              <Badge variant="outline" className="font-normal">
                {DIRECTION_LABELS[template.direction]}
              </Badge>
              {install ? <InstallStatusBadge status={install.status} /> : null}
              {install ? <PrivateLock scope={install.access_scope} className="size-4" /> : null}
            </div>
            <p className="text-muted-foreground text-sm">
              <code>{template.code}</code>
              {template.category ? ` · ${template.category}` : null}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {install ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`${backTo}/${code}/results`)}
            >
              <FileText className="size-4" /> Results
            </Button>
          ) : null}
          {canManage && install ? (
            <>
              <Button size="sm" variant="outline" onClick={openEdit}>
                <Pencil className="size-4" /> Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="size-4" /> Remove
              </Button>
            </>
          ) : null}
          {canManage && !install ? (
            <Button size="sm" onClick={openInstall}>
              <Plus className="size-4" /> Add to team
            </Button>
          ) : null}
          {canManage && template.image ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={setImage.isPending}
              onClick={() => setImage.mutate({ id: template.id, file: null })}
            >
              Reset icon
            </Button>
          ) : null}
        </div>
      </div>

      {/* Body — quickstart + manual (main) + metadata (aside). */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <CollapsibleSection title="Quickstart" defaultOpen>
            <div className="space-y-4">
              <RunInClaude code={template.code} name={template.name} />
              {quickstart ? (
                <div className="border-primary/20 bg-primary/5 rounded-md border p-6">
                  <Markdown copyableCode>{quickstart}</Markdown>
                </div>
              ) : null}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Operating manual">
            {detailQuery.isPending ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : detailQuery.isError ? (
              <p className="text-muted-foreground text-sm">Could not load the operating manual.</p>
            ) : latest && latest.prompt_md.trim() ? (
              <div className="rounded-md border p-6">
                <Markdown>{latest.prompt_md}</Markdown>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                No operating manual is available for this agent.
              </p>
            )}
          </CollapsibleSection>
        </div>

        <aside className="space-y-6">
          <LabeledSection title="What it does">
            <p className="text-sm">{template.summary || 'No summary available for this agent.'}</p>
          </LabeledSection>

          <LabeledSection title="How to use">
            <p className="text-muted-foreground text-sm">{HOW_TO_USE}</p>
          </LabeledSection>

          <LabeledSection title="Details">
            <dl className="space-y-2 text-sm">
              {template.turnaround ? (
                <div className="flex items-start justify-between gap-2">
                  <dt className="text-muted-foreground flex items-center gap-1">
                    Turnaround
                    <InfoHint text="How long, using the agent, to reach a review-ready draft — its output plus your light review. Approximate, not a guarantee." />
                  </dt>
                  <dd className="text-right">{template.turnaround}</dd>
                </div>
              ) : null}
              {template.manual_equiv ? (
                <div className="flex items-start justify-between gap-2">
                  <dt className="text-muted-foreground flex items-center gap-1">
                    Manual equiv.
                    <InfoHint text="How long the same deliverable would take to produce from scratch by hand, without the agent — the time it replaces." />
                  </dt>
                  <dd className="text-right">{template.manual_equiv}</dd>
                </div>
              ) : null}
              {install ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-muted-foreground">Enabled</dt>
                    <dd>{install.enabled ? 'Yes' : 'No'}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-muted-foreground">Visibility</dt>
                    <dd>
                      {install.access_scope === 'members'
                        ? `Private · ${install.access_ids.length} member${
                            install.access_ids.length === 1 ? '' : 's'
                          }`
                        : 'Public'}
                    </dd>
                  </div>
                  {install.allowed_tools.length > 0 ? (
                    <div className="space-y-1">
                      <dt className="text-muted-foreground">Allowed tools</dt>
                      <dd className="flex flex-wrap gap-1">
                        {install.allowed_tools.map((tool) => (
                          <Badge key={tool} variant="secondary" className="font-normal">
                            {tool}
                          </Badge>
                        ))}
                      </dd>
                    </div>
                  ) : null}
                  {install.kb_tags.length > 0 ? (
                    <div className="space-y-1">
                      <dt className="text-muted-foreground">Knowledge-base tags</dt>
                      <dd className="flex flex-wrap gap-1">
                        {install.kb_tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="font-normal">
                            {tag}
                          </Badge>
                        ))}
                      </dd>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd>Not on team</dd>
                </div>
              )}
              {versions.length > 0 ? (
                <div className="space-y-1">
                  <dt className="text-muted-foreground">Versions</dt>
                  <dd className="flex flex-wrap gap-1">
                    {versions.map((v) => (
                      <Badge key={v.id} variant="outline" className="font-normal">
                        {v.version}
                        {v.model_hint ? ` · ${v.model_hint}` : ''}
                      </Badge>
                    ))}
                  </dd>
                </div>
              ) : null}
            </dl>
          </LabeledSection>
        </aside>
      </div>

      {canManage ? (
        <>
          <InstallFormDialog
            key={`${dialogInstall?.id ?? dialogTemplate?.id ?? 'none'}:${dialogOpen}`}
            template={dialogTemplate}
            install={dialogInstall}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove “{template.name}”?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the agent from your team. Staff will no longer see it in the AI Team
                  directory or be able to run it. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={() => {
                    if (install) {
                      deleteInstall.mutate(install.public_id, {
                        onSuccess: () => navigate(backTo),
                      });
                    }
                    setConfirmDelete(false);
                  }}
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
    </div>
  );
}
